///
/// EarthBrowser
///
/// Alert Data Provider
///
/// Provides weather alert data from the local synced dataset table with
/// severity-aware rendering and sorting for the data browser
///
/// Created by Matt Giger on 9/15/2026
/// Copyright (c) 2026 Geodata Labs
///

import type { DataRow, IDataProvider, DataQuery, QueryResult, ColumnMetadata, DataStats, FilterGroup, ProviderCapabilities } from './types'
import type { AlertProperties, AlertSeverity } from '@/earthbrowser/dataset-distribution/alerts/AlertsTable'
import { formatDistanceToNow } from 'date-fns'

const cSeverityRank: Record<AlertSeverity, number> = {
	Extreme: 0,
	Severe: 1,
	Moderate: 2,
	Minor: 3,
	Unknown: 4,
}

const cSeverityClass: Record<AlertSeverity, string> = {
	Extreme: 'text-fuchsia-400',
	Severe: 'text-red-400',
	Moderate: 'text-orange-400',
	Minor: 'text-yellow-300',
	Unknown: 'text-white/50',
}

export class AlertDataProvider implements IDataProvider {
	readonly id: string
	readonly name: string
	readonly type = 'custom' as const
	private queryFn: () => Promise<AlertProperties[]>
	private cachedData: AlertProperties[] = []
	private isInitializedFlag = false

	constructor(config: {
		id: string
		name: string
		queryFn: () => Promise<AlertProperties[]>
	}) {
		this.id = config.id
		this.name = config.name
		this.queryFn = config.queryFn
	}

	async initialize(): Promise<void> {
		if (this.isInitializedFlag) return
		this.cachedData = await this.queryFn()
		this.isInitializedFlag = true
	}

	async refreshData(): Promise<void> {
		this.cachedData = await this.queryFn()
	}

	/**
	 * Update cached data directly (for when data is managed externally)
	 */
	updateCachedData(data: AlertProperties[]): void {
		this.cachedData = data
	}

	readonly capabilities: ProviderCapabilities = {
		supportsFiltering: true,
		supportsSorting: true,
		supportsPagination: true,
		supportsFullTextSearch: true,
		supportsRegex: true,
		supportsGeometry: true,
		supportsInsert: false,
		supportsUpdate: false,
		supportsDelete: false,
		supportsTransactions: false,
		supportsIndexing: false,
		supportsAggregation: false,
		supportsJoins: false,
		isRemote: false,
		supportsStreaming: false,
		supportsCaching: false,
	}

	private columnMetadata: ColumnMetadata[] = [
		{
			id: 'severity',
			name: 'Severity',
			priority: 1,
			minWidth: 100,
			type: 'string',
			nullable: false,
			render: (value: unknown) => {
				const severity = value as AlertSeverity
				return (
					<span className={`font-bold ${cSeverityClass[severity] ?? cSeverityClass.Unknown}`}>
						{severity}
					</span>
				)
			}
		},
		{
			id: 'event',
			name: 'Event',
			priority: 1,
			minWidth: 180,
			type: 'string',
			nullable: false,
			render: (value: unknown) => (
				<div className="font-medium truncate">{value as string}</div>
			)
		},
		{
			id: 'area_desc',
			name: 'Area',
			priority: 2,
			minWidth: 220,
			type: 'string',
			nullable: true,
			render: (value: unknown) => {
				const area = value as string | null
				if (!area) return <span className="text-white/40">—</span>
				return (
					<div className="text-white/70 truncate" title={area}>{area}</div>
				)
			}
		},
		{
			id: 'expires',
			name: 'Expires',
			priority: 3,
			minWidth: 140,
			type: 'date',
			nullable: true,
			render: (value: unknown) => {
				if (!value) return <span className="text-white/40">—</span>
				const date = new Date(value as string)
				const exactTime = `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`
				const expired = date.getTime() < Date.now()
				return (
					<span
						className={expired ? 'text-white/40 cursor-help' : 'text-white/80 cursor-help'}
						title={exactTime}
					>
						{formatDistanceToNow(date, { addSuffix: true })}
					</span>
				)
			}
		},
		{
			id: 'country',
			name: 'Country',
			priority: 4,
			minWidth: 100,
			type: 'string',
			nullable: false,
			render: (value: unknown) => (
				<span className="text-cyan-400/80 uppercase text-xs">{value as string}</span>
			)
		}
	]

	async getColumns(): Promise<ColumnMetadata[]> {
		return this.columnMetadata
	}

	async getStats(): Promise<DataStats> {
		return {
			totalRows: this.cachedData.length,
			columnCount: this.columnMetadata.length,
			memorySize: JSON.stringify(this.cachedData).length,
			lastModified: new Date()
		}
	}

	async query(query: DataQuery): Promise<QueryResult> {
		let filteredData = [...this.cachedData]

		// Apply full-text search
		if (query.search?.query) {
			const lowerSearch = query.search.query.toLowerCase()
			filteredData = filteredData.filter((row) => {
				return Object.values(row).some((value) => {
					if (value == null) return false
					return String(value).toLowerCase().includes(lowerSearch)
				})
			})
		}

		// Apply sorting; severity sorts by rank, not alphabetically
		if (query.sort && query.sort.length > 0) {
			const sortSpec = query.sort[0]
			const sortBy = sortSpec.column
			const sortDirection = sortSpec.direction

			filteredData.sort((a, b) => {
				let comparison = 0
				if (sortBy === 'severity') {
					comparison = (cSeverityRank[a.severity] ?? 4) - (cSeverityRank[b.severity] ?? 4)
				} else {
					const aVal = a[sortBy as keyof AlertProperties]
					const bVal = b[sortBy as keyof AlertProperties]

					if (aVal == null && bVal == null) return 0
					if (aVal == null) return 1
					if (bVal == null) return -1

					if (typeof aVal === 'string' && typeof bVal === 'string') {
						comparison = aVal.localeCompare(bVal)
					} else if (typeof aVal === 'number' && typeof bVal === 'number') {
						comparison = aVal - bVal
					} else {
						comparison = String(aVal).localeCompare(String(bVal))
					}
				}

				return sortDirection === 'desc' ? -comparison : comparison
			})
		}

		// Calculate pagination
		const totalCount = filteredData.length
		const offset = query.offset || 0
		const limit = query.limit || 100
		const rows = filteredData.slice(offset, offset + limit)

		return {
			rows: rows as unknown as DataRow[],
			totalCount,
			offset,
			limit,
			hasMore: offset + limit < totalCount,
			executionTime: 0,
			fromCache: true
		}
	}

	async count(_filters?: FilterGroup): Promise<number> {
		return this.cachedData.length
	}

	async dispose(): Promise<void> {
		this.cachedData = []
		this.isInitializedFlag = false
	}
}
